import { isRecurring, type Cadence } from './period';
import {
  isCustomStepCode,
  isDayBreakStepCode,
  measuringStepFor,
  resolveStepType,
  type QuestStepType,
} from '../quests/step-catalog';

/**
 * WHICH STEPS MAY BACK A BOUNTY, AND WHICH MAY RECUR — one rule for bounties and quests.
 *
 * Every rule here exists to stop one thing: points paid for nothing. The first-touch
 * bounty shipped at 500 points and minted 3.09M in 46 hours, and each of these is a door
 * that would let the same thing happen through a different step.
 *
 * Shared by the api (which enforces it) and the admin (which explains it), so the editor
 * never offers a choice the server will refuse, and the reasons an admin reads are the
 * reasons the server applies.
 */

/**
 * Step codes that are not catalog steps. Their rules live in their own engine — the
 * first-touch bounty's locks, caps and clawback, and the like and comment payouts in the
 * reputation service — so the catalog rules do not apply.
 */
export const ENGINE_STEP_CODES: readonly string[] = [
  'firstTouch',
  'postComment',
  'postLike',
  'commentLike',
  'critiqueLike',
  'critiqueValued',
];

export function isEngineStepCode(stepCode: string): boolean {
  return ENGINE_STEP_CODES.includes(stepCode);
}

/**
 * Why a step may not recur, or null when it may. Read against its MEASURING step.
 *
 * Any measured, period-windowed step may recur (Nick, 2026-10-19: "i want everything
 * opted in"). The two refusals left are structural, not policy: a click step can never
 * complete twice, and a lifetime step would read complete at the start of every period.
 * A step whose act can be undone and redone (an unlike, an unfollow, a withdrawn price)
 * can be re-earned by redoing it each period; the bounty's points and cap are the control.
 */
function recurrenceViolation(measuredBy: QuestStepType): string | null {
  if (measuredBy.completion !== 'measured') {
    return 'Only a measured step can recur — a click completes once, for good.';
  }
  if (measuredBy.scope !== 'since-join') {
    return 'A lifetime step already reads complete when a new period starts, so it would pay every period for nothing.';
  }
  return null;
}

/** What an admin reads when Count to reach is 0, a fraction, or negative. */
export const TARGET_AT_LEAST_ONE = 'Count must be at least 1.';

export type BountyStepUse = {
  cadence: Cadence;
  /** True for a bounty in the onboarding series, where every member it can pay is new. */
  inSeries: boolean;
  /** The bounty's own bar, if it has one. */
  target?: number | null;
};

/**
 * Why a step may not back a bounty used this way, or null when it may.
 *
 * | bounty                    | allowed                                            |
 * | once, in a series         | any measured or click step                         |
 * | once, outside a series    | measured since-join, or click — never lifetime     |
 * | daily / weekly / monthly  | measured, since-join, and on the allowlist         |
 */
export function bountyStepViolation(stepCode: string, use: BountyStepUse): string | null {
  if (isEngineStepCode(stepCode)) return null;
  if (isDayBreakStepCode(stepCode)) return 'A day break is not a step.';
  if (isCustomStepCode(stepCode)) return "A custom step belongs to one quest's wording, so it cannot back a bounty.";

  const step = resolveStepType(stepCode);
  if (!step) return 'There is no step with that code.';
  if (step.productionReady === false) return 'This step is local-only and cannot go live.';

  if (use.target != null) {
    if (step.metric === undefined || step.bar === undefined) {
      return 'This step has no single count, so it cannot take its own target.';
    }
    if (!Number.isInteger(use.target) || use.target < 1) return TARGET_AT_LEAST_ONE;
  }

  if (isRecurring(use.cadence)) return recurrenceViolation(step);

  if (!use.inSeries && step.completion === 'measured' && step.scope === 'lifetime') {
    return 'Outside the onboarding series, a lifetime step would pay every existing member who already qualifies.';
  }
  return null;
}

export type QuestStepRow = {
  stepCode: string;
  enabled: boolean;
  configOverrides?: { measures?: unknown } | null;
};

/**
 * Every reason a quest may not recur, as `"<stepCode>: <reason>"`. Empty means it may.
 *
 * Measured-only is not enough on its own. A recurring quest must touch none of the
 * once-ever state quests keep — the per-step-code click table, the lifelong step chest,
 * the day-gate — and a lifetime step would read 100% the moment each period starts. A
 * custom step is judged by the measurement it BORROWS, exactly as it is scored.
 */
export function recurringQuestViolations(steps: readonly QuestStepRow[], gateStepCode: string | null): string[] {
  const violations: string[] = [];

  if (steps.some((row) => isDayBreakStepCode(row.stepCode))) {
    violations.push('day-break: A recurring quest cannot be split into days.');
  }

  const check = (stepCode: string, overrides?: { measures?: unknown } | null) => {
    const measuredBy = measuringStepFor(stepCode, overrides);
    if (!measuredBy) return;
    const reason = recurrenceViolation(measuredBy);
    if (reason) violations.push(`${stepCode}: ${reason}`);
  };

  for (const row of steps) {
    if (!row.enabled || isDayBreakStepCode(row.stepCode)) continue;
    check(row.stepCode, row.configOverrides);
  }

  if (gateStepCode) check(gateStepCode);
  return violations;
}
